import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import {
  UserIcon,
  Squares2X2Icon,
  ArrowRightOnRectangleIcon,
  HeartIcon,
  UserGroupIcon,
  InformationCircleIcon,
  CalendarDaysIcon,
  SparklesIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import {
  UserIcon as UserIconSolid,
  Squares2X2Icon as Squares2X2IconSolid,
  HeartIcon as HeartIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  SparklesIcon as SparklesIconSolid
} from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/theProposal-logo.png';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  const primaryNavItems = [
    { path: '/home/catalog', label: 'Catalog', Icon: Squares2X2Icon, IconActive: Squares2X2IconSolid },
    { path: '/home/designers', label: 'Designers', Icon: UserGroupIcon, IconActive: UserGroupIconSolid },
    { path: '/home/staff-picks', label: 'Staff Picks', Icon: SparklesIcon, IconActive: SparklesIconSolid },
    { path: '/home/liked-dresses', label: 'Liked', Icon: HeartIcon, IconActive: HeartIconSolid },
  ];

  // Mobile bottom nav keeps Contact Us + Profile so they remain reachable
  // (the desktop utility bar and Account dropdown aren't shown on mobile).
  const mobileNavItems = [
    ...primaryNavItems,
    { path: '/home/information', label: 'Contact Us', Icon: InformationCircleIcon, IconActive: InformationCircleIconSolid },
    { path: '/home/profile', label: 'Profile', Icon: UserIcon, IconActive: UserIconSolid },
  ];

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (!accountOpen) return;
    const onClickAway = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Utility bar (desktop/tablet only) */}
      <div className="hidden md:block bg-gray-50 border-b border-gray-200">
        <div className="max-w-screen-xl mx-auto px-4 flex justify-end">
          <NavLink
            to="/home/information"
            className={({ isActive }) =>
              `flex items-center space-x-1.5 py-1.5 text-xs font-medium transition-colors ${
                isActive ? 'text-primary-600' : 'text-gray-500 hover:text-primary-600'
              }`
            }
          >
            <InformationCircleIcon className="h-4 w-4" />
            <span>Contact Us</span>
          </NavLink>
        </div>
      </div>

      {/* Header with Logo and Desktop Navigation */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-screen-xl mx-auto px-4">
          {/* Mobile Header - Logo Only */}
          <div className="md:hidden py-3 flex justify-center">
            <Link to="/home" className="shrink-0">
              <img
                src={logo}
                alt="The Proposal Bridal"
                className="h-10 sm:h-12 w-auto hover:opacity-90 transition-opacity"
              />
            </Link>
          </div>

          {/* Desktop Header - Logo and Navigation */}
          <div className="hidden md:flex md:items-center md:justify-between py-4">
            <Link to="/home" className="shrink-0">
              <img
                src={logo}
                alt="The Proposal Bridal"
                className="h-12 w-auto hover:opacity-90 transition-opacity"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="flex items-center space-x-6 lg:space-x-8">
              {primaryNavItems.map(({ path, label, Icon, IconActive }) => (
                <NavLink
                  key={path}
                  to={path}
                  end={path === '/home'}
                  className={({ isActive }) =>
                    `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'text-primary-600 bg-primary-50 shadow-sm'
                        : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive ? (
                        <IconActive className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                      <span>{label}</span>
                    </>
                  )}
                </NavLink>
              ))}

              {/* Account Dropdown */}
              <div className="relative" ref={accountRef}>
                <button
                  type="button"
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    accountOpen
                      ? 'text-primary-600 bg-primary-50 shadow-sm'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  <UserIcon className="h-5 w-5" />
                  <span>Account</span>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${accountOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {accountOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-44 rounded-lg border border-gray-200 bg-white shadow-lg py-1 z-50"
                  >
                    <NavLink
                      to="/home/profile"
                      role="menuitem"
                      onClick={() => setAccountOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                          isActive
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                        }`
                      }
                    >
                      <UserIcon className="h-4 w-4" />
                      <span>Profile</span>
                    </NavLink>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>
      
      <main className="max-w-screen-xl mx-auto">
        <Outlet />
      </main>

      {/* Floating Make Appointment Button */}
      <Link
        to="/home/information"
        className="fixed right-4 top-[15%] z-40 flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
      >
        <CalendarDaysIcon className="h-5 w-5" />
        <span className="hidden sm:inline">Make Appointment</span>
        <span className="sm:hidden">Book</span>
      </Link>
      
      {/* Bottom Navigation - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around items-center h-16">
          {mobileNavItems.map(({ path, label, Icon, IconActive }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/home'}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center flex-1 h-full px-2 touch-target ${
                  isActive ? 'text-primary-500' : 'text-gray-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive ? (
                    <IconActive className="h-6 w-6" />
                  ) : (
                    <Icon className="h-6 w-6" />
                  )}
                  <span className="text-xs mt-1">{label.split(' ')[0]}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;